import {
  AssetRoutineState,
  CounterQuestionConfig,
  CounterResponseState,
  CounterState,
  CounterType,
  roundDecimals,
  RoutineState,
  WorkflowRoutineState,
} from '@gripp/shared-logic';
import { useEffect, useState } from 'react';
import { useController } from 'react-hook-form';
import { StyleSheet } from 'react-native';
import { Colors } from '../../../../../themes/colors';
import { useFormContext } from '../../../../forms';
import { CounterExecutionComponent } from '../../../counter/counterExecutionComponent';
import {
  RoutineQuestionExecutionForm,
  RoutineQuestionExecutionFormProps,
} from '../types';

export const CounterQuestionExecutionForm: RoutineQuestionExecutionForm<
  CounterQuestionConfig,
  CounterResponseState
> = (
  props: RoutineQuestionExecutionFormProps<
    CounterQuestionConfig,
    CounterResponseState
  >
) => {
  const fieldPrefix = `responses.${props.responseIndex}`;
  const formKey = `${fieldPrefix}.count`;
  const oldValueKey = `${fieldPrefix}.oldValue`;
  const userCountKey = `${fieldPrefix}.userCount`;
  const counterTypeKey = `${fieldPrefix}.counterType`;
  const oumKey = `${fieldPrefix}.uom`;

  const { control, setValue, getValues } = useFormContext();
  const { field } = useController({
    control: control,
    name: formKey,
  });

  const [initialValue, setInitialValue] = useState(0);

  const getStateCount = (
    responseIndex: number,
    currentState: AssetRoutineState<RoutineState> | undefined
  ) => {
    const responses = (currentState?.state as WorkflowRoutineState)?.responses;

    if (responses && responseIndex < responses.length) {
      const response = responses[responseIndex] as unknown;
      return (response as CounterState)?.counterType === CounterType.Simple
        ? 0
        : (response as CounterState)?.count;
    }

    return (currentState?.state as CounterState)?.counterType ===
      CounterType.Simple
      ? 0
      : (currentState?.state as CounterState)?.count;
  };

  useEffect(() => {
    const count = getStateCount(props.responseIndex, props.currentState);
    if (count !== undefined && count !== null) {
      setInitialValue(count);
    }
  }, [props.currentState]);

  useEffect(() => {
    const truncNumber = roundDecimals(
      initialValue,
      props.config.decimalPlaces ?? 0
    );
    if (props.currentState?.state) {
      const prior = getStateCount(props.responseIndex, props.currentState);
      if (prior !== undefined && prior !== null) {
        setValue(oldValueKey, truncNumber, {
          shouldDirty: false,
          shouldValidate: false,
        });
      } else {
        setValue(oldValueKey, undefined as any, {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
    } else {
      setValue(oldValueKey, undefined as any, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
    setValue(counterTypeKey, props.config.counterType, {
      shouldDirty: false,
      shouldValidate: false,
    });
    setValue(oumKey, props.config.uom, {
      shouldDirty: false,
      shouldValidate: false,
    });
    if (getValues(formKey) === undefined) {
      setValue(userCountKey, undefined as any, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [initialValue]);

  const handleChange = (value: number) => {
    if (value === null || Number.isNaN(value)) {
      field.onChange(undefined as any);
      setValue(userCountKey, undefined as any, {
        shouldDirty: true,
        shouldValidate: true,
      });
      return;
    }
    const truncNumber = roundDecimals(value, props.config.decimalPlaces ?? 0);
    const baseline = roundDecimals(
      getValues(oldValueKey) ?? 0,
      props.config.decimalPlaces ?? 0
    );
    const delta = roundDecimals(
      truncNumber - baseline,
      props.config.decimalPlaces ?? 0
    );
    field.onChange(truncNumber);
    setValue(userCountKey, delta, { shouldDirty: true, shouldValidate: true });
  };

  return (
    <CounterExecutionComponent
      onBlur={field.onBlur}
      instructions={''}
      initialValue={initialValue}
      onChange={handleChange}
      value={field.value}
      formKeyPrefix={fieldPrefix}
      autoFocus={false}
      decimalPlaces={
        props.config.decimal ? props.config.decimalPlaces : undefined
      }
      containerStyle={styles.container}
      inputContainerStyle={styles.inputContainerStyle}
      pillToggleContainerStyle={styles.pillToggleContainer}
      counterType={props.config.counterType}
      isIssueEditRoutine={props.isIssueEditRoutine}
    />
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.white,
    borderRadius: 12,
  },
  inputContainerStyle: {
    marginTop: 0,
  },
  pillToggleContainer: {
    paddingTop: 0,
    marginTop: 10,
  },
});
